import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ENUM_ActivityEventType } from '@api-new/common';
import { HTTP_AP_RecordActivityEvent_Request_ActivityEvent, ReportingApiService } from '@api-new/reportingservice';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { AnalyticsService } from '@shared/services/analytics.service';
import { forceSentAnalyticsEventsInBuffer, logAnalyticsEvent, sendLogEvents } from '@shared/store/analytics/analytics.actions';
import { selectPlatformState } from '@shared/store/platform/platform.selectors';
import dayjs from 'dayjs';
import { delay, merge, timer } from 'rxjs';
import { buffer, filter, map, switchMap } from 'rxjs/operators';
import * as uuid from 'uuid';

const BUFFER_TIME = 30000; // (30sec) Buffer time over which events are collected for before being sent to BE

@Injectable()
export class AnalyticsEffects {
  private actions$ = inject(Actions);
  private analyticsService = inject(AnalyticsService);
  private reportingApiService = inject(ReportingApiService);
  private title = inject(Title);
  private globalStore = inject(Store);

  trackRouterChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      delay(0), // delay is necessary to avoid problems with sending old title after routerNavigated
      map(({ payload }) =>
        logAnalyticsEvent({
          event: {
            type: ENUM_ActivityEventType.ACTIVITY_EVENT_TYPE_ROUTE,
            route: location.origin + payload.event.urlAfterRedirects,
            name: this.title.getTitle(),
          },
        }),
      ),
    ),
  );

  bufferAndSendEvents$ = createEffect(() => {
    const flushSignal$ = this.actions$.pipe(ofType(forceSentAnalyticsEventsInBuffer));
    const timer$ = timer(BUFFER_TIME, BUFFER_TIME);

    const closingNotifier$ = merge(flushSignal$, timer$);

    return this.actions$.pipe(
      ofType(logAnalyticsEvent),
      concatLatestFrom(() => this.globalStore.select(selectPlatformState)),
      map(([{ event }, platformState]) => {
        const sessionId = this.analyticsService.getSessionId();
        return {
          ...event,
          eventId: uuid.v4(),
          route: event.route ?? location.href,
          timestamp: dayjs().toISOString(),
          sessionId,
          device: navigator.userAgent,
          ...(platformState && { advisorUserId: platformState?.adviserUser?.id, companyId: platformState?.company?.id }),
        } as HTTP_AP_RecordActivityEvent_Request_ActivityEvent;
      }),
      buffer(closingNotifier$),
      filter((actions) => actions.length > 0),
      map((events) => sendLogEvents({ events })),
    );
  });

  sendEventsToServer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sendLogEvents),
        switchMap(({ events }) => this.reportingApiService.HTTP_AP_RecordActivityEvent({ activityEvents: events }, { keepalive: true })),
      ),
    {
      dispatch: false,
    },
  );
}
